<script>
    import { onDestroy, onMount } from "svelte";

    import { apiMethods } from "../../../Services/index.svelte";

    import {
        EncounterSidePanelProps,
        postOpSurveys,
        preOpQuestionnaires,
        user,
    } from "../../../stores";

    import {
        getText,
        getFormatDate,
        getFormatDateAndTime,
        getTitle,
        getEncounterBillingType,
        getEncounterNeuraxialType,
        goToURL,
        isObjectEmpty,
        showError,
        getTimeDiffInMillSec,
        getEncounterDischargeDateWithFallBackDuration,
        getDate,
    } from "../../../Utils/utilFunctions.svelte";

    import {
        CardLayout,
        DateInput,
        NotificationModal,
        TableElementEditableStatus,
    } from "../../../Components/Common/index.svelte";

    import {
        cachePostOpSurvey,
        cachePreOpInstructions,
        cachePreOpQuestionnaire,
    } from "../../../Utils/utilCache.svelte";

    import { getTimeAfterChanges } from "../../Calendar/helpers/calendarHelper.svelte";

    import { eyeIcon } from "../../../Utils/utilConstants.svelte";

    export let procedureDetails = {};
    export let doctorDetails = {};
    export let patientEncounter = {};
    export let doctorProviders = [];
    export let disableChangeProviderNo = false;
    export let isFromCalendarList = false;
    export let reloadDoctorDetails = () => {};
    export let reloadProcedureDetails = () => {};

    $: isUserAlwaysEnableChangeProviderNumber =
        $user.role == 100 || $user.role == 80 || $user.role == 20;

    $: encounterBillingStatus = patientEncounter.billingStatus;

    $: hospitalDetails = { ...procedureDetails.hospital };

    $: surgeonDetails = { ...procedureDetails.surgeon };

    $: _syncDoctorProviderOptions(doctorProviders);

    $: isEncounterHasValidScheduledDate = procedureDetails?.scheduledDate
        ? procedureDetails.scheduledDate.split("T")[0].split("-")[0] >= 1900
        : false;

    let doctorProviderOptions = [];

    let enableEditScheduledDate = false,
        showConfirmUpdateScheduledDateModal = false,
        isUpdateScheduledDateLoading = false;

    let clickedInside = false;

    onMount(async () => {
        document.addEventListener("pointerdown", handleClickOutside);

        await cachePreOpQuestionnaire();
        await cachePreOpInstructions();
        await cachePostOpSurvey();
    });

    onDestroy(() => {
        document.removeEventListener("pointerdown", handleClickOutside);
    });

    const handleClickOutside = () => {
        if (!clickedInside) _disableEditScheduledDate();

        clickedInside = false;
    };

    const handleScheduledDateChange = async () => {
        clickedInside = true;
        isUpdateScheduledDateLoading = true;

        const newEncounterTimes = {
            scheduledDate: procedureDetails.tempScheduledDate,
        };

        const timeDiff = getTimeDiffInMillSec(
            procedureDetails.tempScheduledDate,
            patientEncounter.scheduledDate,
        );
        if (timeDiff !== 0 && patientEncounter.dischargeDate) {
            newEncounterTimes.dischargeDate = getTimeAfterChanges(
                getEncounterDischargeDateWithFallBackDuration(patientEncounter),
                timeDiff,
            );
        }

        await apiMethods.putMethod
            .updateEncounterScheduleDate(patientEncounter.id, newEncounterTimes)
            .then(async (res) => {
                if (!res || res.error) throw res.error;
                else await reloadProcedureDetails();
            })
            .catch((err) =>
                showError(
                    err ??
                        "Failed to update encounter scheduled date, please refresh the page and try again and contact support if the issue persists.",
                ),
            );

        _disableEditScheduledDate();
        isUpdateScheduledDateLoading = false;

        _closeConfirmUpdateScheduledDateModal();
        clickedInside = false;
    };

    const viewPreOpQuestionnaire = () => {
        const targetQuestionnaire = $preOpQuestionnaires.find(
            (q) => q.id == patientEncounter.preOpQuestionnaireId,
        );

        if (!targetQuestionnaire || isObjectEmpty(targetQuestionnaire)) return;

        let url = targetQuestionnaire.url;

        if (url.includes("?"))
            url += `&q=${targetQuestionnaire.id}&n=${patientEncounter.id}`;
        else url += `?q=${targetQuestionnaire.id}&n=${patientEncounter.id}`;

        window.open(url, "_blank");
    };

    const viewPostOpSurvey = () => {
        const targetSurvey = $postOpSurveys.find(
            (s) => s.id == patientEncounter.postOpSurveyId,
        );

        if (!targetSurvey || isObjectEmpty(targetSurvey)) return;

        let url = targetSurvey.url;

        if (url.includes("?"))
            url += `&s=${targetSurvey.id}&n=${patientEncounter.id}`;
        else url += `?s=${targetSurvey.id}&n=${patientEncounter.id}`;
    };

    // HELPER FUNCTIONS
    const _getEncounterTypeName = () => {
        const { typeOfBill, neuraxialType } = procedureDetails;

        if (typeOfBill != 2 || !neuraxialType)
            return getEncounterBillingType(typeOfBill);

        return `${getEncounterBillingType(typeOfBill)} (${getEncounterNeuraxialType(neuraxialType)})`;
    };

    const _getDisplayDate = (date) => {
        if (!date) return "";

        const formatDate = getFormatDateAndTime(date);

        const formatDateArr = formatDate.split(", ");

        return formatDateArr[1] == "00:00" ? formatDateArr[0] : formatDate;
    };

    const _getDoctorFullName = () => {
        const { firstName, lastName, title } = doctorDetails;

        return `${getTitle(title)} ${firstName} ${lastName}`;
    };

    const _redirectToDoctor = () => {
        goToURL(`/Account/view/${doctorDetails.id}`, isFromCalendarList);
    };

    const _redirectToHospital = () => {
        goToURL(`/Hospitals/view/${hospitalDetails.id}`, isFromCalendarList);
    };

    const _getSurgeonName = () => {
        const { firstName, lastName, title, speciality } = surgeonDetails;

        return `${getTitle(title)} ${firstName} ${lastName} ${speciality ? `(${speciality})` : ""}`;
    };

    const _syncDoctorProviderOptions = (arr = []) => {
        if (!arr.length) return;

        arr.map((provider) => {
            doctorProviderOptions.push({
                id: provider.id,
                type: provider.number,
            });
        });
    };

    const _getDoctorProviderAddress = () => {
        const { locationAddress, locationAddress2, city, state, postCode } =
            doctorDetails;

        const res = [];

        if (locationAddress) res.push(locationAddress);
        if (locationAddress2) res.push(locationAddress2);
        if (city) res.push(city);
        if (state) res.push(state);
        if (postCode) res.push(postCode);

        return res.join(", ");
    };

    const _enableEditScheduledDate = () => {
        enableEditScheduledDate = true;

        procedureDetails.tempScheduledDate =
            new Date(procedureDetails.scheduledDate) <=
            new Date("1900-01-01T00:00:00")
                ? new Date()
                : procedureDetails.scheduledDate;
    };

    const _disableEditScheduledDate = () => {
        enableEditScheduledDate = false;

        procedureDetails.tempScheduledDate = procedureDetails.scheduledDate;
    };

    const _openConfirmUpdateScheduledDateModal = () => {
        showConfirmUpdateScheduledDateModal = true;
        clickedInside = true;
    };

    const _closeConfirmUpdateScheduledDateModal = () => {
        showConfirmUpdateScheduledDateModal = false;
        clickedInside = true;
    };

    const _viewEncounterOnScheduledDate = () => {
        const { scheduledDate } = procedureDetails;

        if (!scheduledDate || !isEncounterHasValidScheduledDate) return;

        goToURL(
            `/PatientEncounter/page=0&size=25&doctorId=${doctorDetails.id}&fromDate=${getDate(scheduledDate, "-", true)}&toDate=${getDate(scheduledDate, "-", true)}`,
            isFromCalendarList,
        );
    };
</script>

<CardLayout title="Procedure Details">
    <div
        class="flex flex-col flex-1 px-2 py-4 w-full lg:flex-row overflow-visible"
    >
        <div class="flex flex-col flex-1 gap-y-4 h-full">
            <div class="flex flex-row gap-x-2">
                <p class="title">Encounter Type:</p>
                <p class="content">
                    {_getEncounterTypeName()}
                </p>
            </div>

            <div class="flex flex-row gap-x-2">
                <p class="title">Procedure Name:</p>
                <p class="content">
                    {getText(procedureDetails.procedureName)}
                </p>
            </div>

            <div class="flex flex-row items-center gap-x-2">
                <p class="title">Scheduled Date:</p>
                <div class="content flex gap-x-2 items-center">
                    {#if !enableEditScheduledDate}
                        <p
                            class="cursor-pointer underline"
                            on:click={_enableEditScheduledDate}
                        >
                            {_getDisplayDate(procedureDetails.scheduledDate)}
                        </p>
                    {:else}
                        <div
                            id="date-input-wrapper"
                            on:pointerdown={() => (clickedInside = true)}
                        >
                            <DateInput
                                showTime
                                minDate="1900-01-01T00:00:00"
                                wrapperClass="input input-sm input-bordered"
                                wrapperClassFocus="inputFocus"
                                bind:value={procedureDetails.tempScheduledDate}
                                handleInput={_openConfirmUpdateScheduledDateModal}
                            />
                        </div>
                    {/if}

                    {#if procedureDetails.scheduledDate}
                        <div
                            class="viewIcon tooltip tooltip-secondary tooltip-left flex items-center justify-center w-[30px] h-[30px] cursor-pointer hover:rounded-full hover:bg-secondary hover:text-white"
                            data-tip="Display all encounters for the day"
                            on:click|preventDefault={_viewEncounterOnScheduledDate}
                        >
                            <i class={eyeIcon}></i>
                        </div>
                    {/if}
                </div>
            </div>

            <div class="flex flex-row items-center gap-x-2">
                <p class="title">PreOp Questionnaire:</p>
                <TableElementEditableStatus
                    type="patientEncounterPreOpQuestionnaire"
                    row={{
                        id: patientEncounter.id,
                        questionnaireId: patientEncounter.preOpQuestionnaireId,
                        encounterScheduledDate: patientEncounter.scheduledDate,
                    }}
                    onFinish={() => reloadDoctorDetails()}
                />
                {#if patientEncounter.preOpQuestionnaireId}
                    <div
                        class="cursor-pointer tooltip tooltip-secondary tooltip-right flex items-center justify-center w-[30px] h-[30px] hover:rounded-full hover:bg-secondary hover:text-white"
                        data-tip="View PreOp Questionnaire"
                        on:click={viewPreOpQuestionnaire}
                    >
                        <i class="fa-regular fa-eye"></i>
                    </div>
                {/if}
            </div>

            <div class="flex flex-row items-center gap-x-2">
                <p class="title">PreOp Instruction:</p>
                <TableElementEditableStatus
                    type="patientEncounterPreOpInstruction"
                    row={{
                        id: patientEncounter.id,
                        instructionId: patientEncounter.preOpInstructionsId,
                        doctor: patientEncounter.doctor,
                        enableDoctorEdit: true,
                    }}
                    onFinish={() => reloadDoctorDetails()}
                />
            </div>

            <!-- <div class="flex flex-row gap-x-2">
                <p class="title">PostOp Survey:</p>
                <TableElementEditableStatus
                    type="patientEncounterPostOpSurvey"
                    row={{
                        id: patientEncounter.id,
                        surveyId: patientEncounter.postOpSurveyId,
                    }}
                    onFinish={() => reloadDoctorDetails()}
                />
                {#if patientEncounter.postOpSurveyId}
                    <div
                        class="cursor-pointer tooltip tooltip-secondary tooltip-right flex items-center justify-center w-[30px] h-[30px] hover:rounded-full hover:bg-secondary hover:text-white"
                        data-tip="View PostOp Survey"
                        on:click={viewPostOpSurvey}
                    >
                        <i class="fa-regular fa-eye"></i>
                    </div>
                {/if}
            </div> -->
        </div>

        <div class="divider lg:divider-horizontal" />

        <div class="flex flex-col flex-1 gap-y-4 h-full">
            <div class="flex flex-row gap-x-2">
                <p class="title">Doctor:</p>
                <p
                    class="content plink"
                    on:click|preventDefault={_redirectToDoctor}
                >
                    {_getDoctorFullName()}
                </p>
            </div>

            <div class="flex flex-row gap-x-2">
                <p class="title">Provider No.:</p>

                {#if encounterBillingStatus == 5 || (disableChangeProviderNo && !isUserAlwaysEnableChangeProviderNumber)}
                    <div
                        class="flex flex-1 gap-x-2 items-center cursor-default"
                    >
                        <p>{doctorDetails.number}</p>
                        <span
                            class="tooltip tooltip-secondary tooltip-right"
                            data-tip={`Provider number cannot be changed  ${encounterBillingStatus == 5 ? "for completed encounter." : "after a quote or invoice has been created for the encounter."}`}
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                class="stroke-secondary shrink-0 w-5 h-5"
                            >
                                <path
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    stroke-width="2"
                                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                />
                            </svg>
                        </span>
                    </div>
                {:else}
                    <TableElementEditableStatus
                        type="patientEncounterProviderNo"
                        row={{
                            data: patientEncounter,
                            selectedNumber: doctorDetails.number,
                            options: doctorProviderOptions,
                        }}
                        onFinish={() => {
                            reloadDoctorDetails(patientEncounter.id);
                        }}
                    />
                {/if}
            </div>
            <!-- 
            <div class="flex flex-row gap-x-2">
                <p class="title">Provider Location.:</p>
                <p class="content">{getText(doctorDetails.location)}</p>
            </div>

            <div class="flex flex-row gap-x-2">
                <p class="title">Provider Address:</p>
                <p class="content">{_getDoctorProviderAddress()}</p>
            </div> -->

            {#if hospitalDetails.id}
                <div class="flex flex-row gap-x-2">
                    <p class="title">Hospital Name:</p>
                    <p
                        class="content plink"
                        on:click|preventDefault={_redirectToHospital}
                    >
                        {getText(hospitalDetails.name)}{hospitalDetails.state
                            ? `, ${hospitalDetails.state}`
                            : ""}
                    </p>
                </div>
            {:else}
                <p>No Hospital Information...</p>
            {/if}

            {#if surgeonDetails.id}
                <div class="flex flex-row gap-x-2">
                    <p class="title">Surgeon/Referrer Name:</p>
                    <p class="content">{_getSurgeonName()}</p>
                </div>
            {:else}
                <p>No Surgeon/Referrer Information...</p>
            {/if}
        </div>

        <div class="divider lg:divider-horizontal" />

        <div class="flex flex-col flex-1 gap-y-4 h-full">
            <div class="flex flex-row gap-x-2">
                <p class="title">Encounter Created Date:</p>
                <p class="content">
                    {getFormatDate(procedureDetails.createdDate)}
                </p>
            </div>

            <div class="flex flex-row gap-x-2">
                <p class="title">Last Updated Date:</p>
                <p class="content">
                    {getFormatDate(procedureDetails.updatedDate)}
                </p>
            </div>
        </div>
    </div>
</CardLayout>

<NotificationModal
    title="Update Scheduled Date"
    isModalOpen={showConfirmUpdateScheduledDateModal}
    cancelText="Not now"
    closeModal={_closeConfirmUpdateScheduledDateModal}
    closeModalFunction={_closeConfirmUpdateScheduledDateModal}
    submitText={isUpdateScheduledDateLoading ? "Updating..." : "Update"}
    confirmAction={handleScheduledDateChange}
    isConfirming={isUpdateScheduledDateLoading}
>
    <p class="leading-6 p-4">
        Update encounter scheduled date to {_getDisplayDate(
            procedureDetails.tempScheduledDate,
        )}
    </p>
    {#if patientEncounter.estimates.length > 0 || patientEncounter.invoices.length > 0}
        <p class="leading-6 p-4">
            Changing the scheduled date may result in different insurance rebate for the existing quote/invoice. Please consider regenerating any existing quote/invoice
        </p>
    {/if}
</NotificationModal>

<style>
    .title {
        width: 200px;
    }

    .content {
        width: calc(100% - 200px);
    }
</style>
